import { useEffect, useState } from 'react';
import type { PriWord } from '@/copy/hero-pri-words';

export type SwapPhase = 'typing' | 'holdEn' | 'toAr' | 'holdAr' | 'toEn' | 'clearing';

export interface UseSwapStateOptions {
  words: readonly PriWord[];
  prefix: string;
  typeDelay?: number;
  backDelay?: number;
  holdEnDelay?: number;
  holdArDelay?: number;
  finalHoldArDelay?: number;
  flipDuration?: number;
  withClearing?: boolean;
}

export interface SwapState {
  phase: SwapPhase;
  wordIndex: number;
  charCount: number;
}

export function useSwapState(opts: UseSwapStateOptions): SwapState {
  const {
    words,
    prefix,
    typeDelay = 95,
    backDelay = 48,
    holdEnDelay = 500,
    holdArDelay = 1100,
    finalHoldArDelay = 2400,
    flipDuration = 480,
    withClearing = true,
  } = opts;

  const [wordIndex, setWordIndex] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [phase, setPhase] = useState<SwapPhase>('typing');

  useEffect(() => {
    const current = words[wordIndex];
    if (!current) return;
    const suffix = current.en.slice(prefix.length);
    const isLast = wordIndex === words.length - 1;

    if (phase === 'typing') {
      if (charCount < suffix.length) {
        const t = setTimeout(() => setCharCount((c) => c + 1), typeDelay);
        return () => clearTimeout(t);
      }
      const t = setTimeout(() => setPhase('holdEn'), holdEnDelay);
      return () => clearTimeout(t);
    }
    if (phase === 'holdEn') {
      const t = setTimeout(() => setPhase('toAr'), holdEnDelay);
      return () => clearTimeout(t);
    }
    if (phase === 'toAr') {
      const t = setTimeout(() => setPhase('holdAr'), flipDuration);
      return () => clearTimeout(t);
    }
    if (phase === 'holdAr') {
      const t = setTimeout(() => setPhase('toEn'), isLast ? finalHoldArDelay : holdArDelay);
      return () => clearTimeout(t);
    }
    if (phase === 'toEn') {
      const t = setTimeout(
        () => setPhase(withClearing ? 'clearing' : 'typing'),
        flipDuration
      );
      return () => clearTimeout(t);
    }
    if (phase === 'clearing') {
      if (charCount > 0) {
        const t = setTimeout(() => setCharCount((c) => c - 1), backDelay);
        return () => clearTimeout(t);
      }
      const t = setTimeout(() => {
        setWordIndex((i) => (i + 1) % words.length);
        setPhase('typing');
      }, 180);
      return () => clearTimeout(t);
    }
  }, [
    phase,
    charCount,
    wordIndex,
    words,
    prefix,
    typeDelay,
    backDelay,
    holdEnDelay,
    holdArDelay,
    finalHoldArDelay,
    flipDuration,
    withClearing,
  ]);

  return { phase, wordIndex, charCount };
}
